import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtBRL, calcTroco, sugerirTrocoPara, STATUS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { ChatDialog } from "@/components/ChatDialog";
import { LoteDialog } from "@/components/LoteDialog";
import { ImportDialog } from "@/components/ImportDialog";
import { EvidenceViewer } from "@/components/EvidenceViewer";
import { useChatUnread } from "@/lib/useChatUnread";
import { PasswordConfirm } from "@/components/PasswordConfirm";

import { SEM_TROCO_FORMAS, buildPedidoLabel, localDateStr } from "./entregas/entregasHelpers";
import { EntregasForm } from "./entregas/EntregasForm";
import { EntregasHeader } from "./entregas/EntregasHeader";
import { EntregasBody } from "./entregas/EntregasBody";
import { EMPTY_FILTERS } from "./entregas/EntregasFilters";
import { OtimizarDialog } from "./entregas/OtimizarDialog";
import { useEntregasData } from "./entregas/useEntregasData";
import { useEntregasSelection } from "./entregas/useEntregasSelection";
import { computeDuplicateInfo, dupWindowStart } from "@/components/lote/loteHelpers";
import { isAdminLike, hasPerm } from "@/lib/roles";
import { getMissingRequiredFields, buildErrorMessage } from "./entregas/entregasValidation";

/**
 * Entregas — Orquestrador principal. Fev/2026: refatorado de 449 → ~210 linhas.
 * Estado de dados via `useEntregasData` (I/O + polling), seleção múltipla via
 * `useEntregasSelection`. UI dividida em EntregasHeader + EntregasBody +
 * componentes de diálogo. CRUD unitário e mutações de status permanecem aqui
 * porque compartilham state local do formulário (Nova/Editar).
 */
export default function Entregas() {
  const { user: currentUser } = useAuth();
  const canConfigHorario = isAdminLike(currentUser) || (currentUser?.permissions || []).includes("config_horario");
  // Trava UI matriz-first: sem entregas.i não vê botões (backend também bloqueia — defesa em profundidade)
  const canCreate = hasPerm(currentUser, "entregas", "i");

  // ---- Filtros + paginação (server-side, iter 94) ----
  const todayStr = localDateStr();
  const tomorrowStr = localDateStr(new Date(Date.now() + 86400000));
  const [filters, setFilters] = useState(() => {
    // Iter 99: deep-link `?rota_id=<id|__none__>` (vindo da tela de Rotas) abre já filtrado, período "todas".
    const qp = new URLSearchParams(window.location.search);
    const rotaQ = qp.get("rota_id");
    return { ...EMPTY_FILTERS, start: rotaQ ? "" : todayStr, end: rotaQ ? "" : todayStr, rota_id: rotaQ ? [rotaQ] : [] };
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  // Iter 95: ordenação server-side. null = padrão do servidor (created_at desc).
  // Ciclo por coluna: direção inicial → inversa → sem ordenação.
  const [sort, setSort] = useState(null);
  const toggleSort = (by) => setSort((s) => {
    const first = ["data", "created_at", "valor", "hr_saida", "hr_chegada"].includes(by) ? "desc" : "asc";
    if (s?.by !== by) return { by, dir: first };
    if (s.dir === first) return { by, dir: first === "asc" ? "desc" : "asc" };
    return null;
  });
  const filtersKey = JSON.stringify(filters);
  useEffect(() => { setPage(1); }, [filtersKey, pageSize, sort]);

  // ---- Dados + Ações CRUD ----
  const { items, total, loading, programadasCount, entregadores, bairros, regioes, tipos, formas, rotas, empresa, setEmpresa, load, onCatalogAdd, reloadRotas } = useEntregasData(filters, { page, pageSize, sort });

  // ---- Estado UI local ----
  const [open, setOpen] = useState(false);
  const [loteOpen, setLoteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [chatEntrega, setChatEntrega] = useState(null);
  const { unreadMap, markRead } = useChatUnread();
  const [form, setForm] = useState(initial);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [statusPrompt, setStatusPrompt] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [pwdConfirm, setPwdConfirm] = useState(null);
  // Duplicidade fev/2026 v4.1 — refetch por data + memo pra Nova/Editar Entrega
  const [dbForFormDate, setDbForFormDate] = useState([]);
  const [dbForEditDate, setDbForEditDate] = useState([]);

  useEffect(() => {
    if (!open || !form.data) return;
    let cancelled = false;
    const start = dupWindowStart(form.data);
    api.get(`/entregas?start=${start}`)
      .then((r) => { if (!cancelled) setDbForFormDate(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setDbForFormDate([]); });
    return () => { cancelled = true; };
  }, [open, form.data]);

  useEffect(() => {
    if (!editItem || !editForm.data) return;
    let cancelled = false;
    const start = dupWindowStart(editForm.data);
    api.get(`/entregas?start=${start}`)
      .then((r) => { if (!cancelled) setDbForEditDate(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setDbForEditDate([]); });
    return () => { cancelled = true; };
  }, [editItem, editForm.data]);

  const formDup = useMemo(() => {
    if (!open) return null;
    const info = computeDuplicateInfo([form], dbForFormDate, form.data);
    return info.get(0) || null;
  }, [open, form, dbForFormDate]);

  const editDup = useMemo(() => {
    if (!editItem) return null;
    const filtered = dbForEditDate.filter((e) => e.id !== editItem.id);
    const info = computeDuplicateInfo([editForm], filtered, editForm.data);
    return info.get(0) || null;
  }, [editItem, editForm, dbForEditDate]);

  function initial() {
    const today = localDateStr();
    return {
      entregador_id: "", bairro_id: "", regiao_id: "", rota_id: "", tipo_id: "", forma_pgto_id: "",
      numero_pedido: "", numero_pedido_receita: "", numero_pedido_revenda: "",
      qtd_pedidos: 1, pedidos_multiplos: [],
      cliente_nome: "", cliente_telefone: "", buscar_receita: false,
      valor: "", valor_recebido: "", troco_para: "", troco: 0, status: "em_transito", motivo: "", observacao: "",
      data: today, prioridade: "normal",
    };
  }

  // ---- Filtros + derivações memoizadas ----
  const setQuickFilter = (kind) => {
    if (kind === "hoje") setFilters((f) => ({ ...f, start: todayStr, end: todayStr }));
    else if (kind === "amanha") setFilters((f) => ({ ...f, start: tomorrowStr, end: tomorrowStr }));
    else if (kind === "programadas") setFilters((f) => ({ ...f, start: tomorrowStr, end: "" }));
    else if (kind === "todas") setFilters((f) => ({ ...f, start: "", end: "" }));
  };

  // Iter 94: filtros aplicados no servidor — `items` já é a página filtrada.
  const filtered = items;

  // ---- Seleção múltipla + bulk actions ----
  const { selected, bulkEntregador, setBulkEntregador, bulkData, setBulkData, bulkRota, setBulkRota, toggleSelect, toggleSelectAll,
    clearSelection, bulkAssignEntregador, bulkUpdateData, bulkAssignRota, bulkDeleteSelected, deleteAllVisible } = useEntregasSelection(filtered, load, { onRotaAssigned: reloadRotas });

  // Iter 99 (Bloco 5): otimiza a sequência (OSRM) das entregas selecionadas — exige mesmo entregador.
  const [otimResult, setOtimResult] = useState(null);
  const [otimBusy, setOtimBusy] = useState(false);
  const otimizarSelecionadas = async () => {
    if (selected.size < 2) return toast.error("Selecione ao menos 2 entregas do mesmo entregador");
    setOtimBusy(true);
    try {
      const r = await api.post("/entregas/otimizar", { ids: Array.from(selected), persist: true });
      setOtimResult(r.data);
      clearSelection(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao otimizar sequência"); }
    finally { setOtimBusy(false); }
  };

  // ---- Helpers de tipo/forma ----
  const isColeta = (tipo_id) => (tipos.find((x) => x.id === tipo_id)?.nome?.toLowerCase() || "").includes("coleta");
  const isReceitaRevenda = (tipo_id) => {
    const t = tipos.find((x) => x.id === tipo_id)?.nome?.toLowerCase() || "";
    return t.includes("receita") && t.includes("revenda");
  };
  const isSemTroco = (forma_pgto_id) => {
    const nome = formas.find((f) => f.id === forma_pgto_id)?.nome?.toLowerCase() || "";
    return SEM_TROCO_FORMAS.some((s) => nome.includes(s));
  };
  const nameOf = (arr, id) => arr.find((x) => x.id === id)?.nome || "—";

  const setField = (k, v, target = "form") => {
    const setter = target === "form" ? setForm : setEditForm;
    setter((prev) => {
      const nf = { ...prev, [k]: v };
      if (k === "bairro_id") {
        const b = bairros.find((x) => x.id === v);
        if (b?.regiao_id) nf.regiao_id = b.regiao_id;
        // Iter 98: bairro mudou → rota volta pra automática (backend resolve pelo bairro;
        // se o bairro estiver em várias rotas o campo Rota aparece pro usuário escolher).
        nf.rota_id = "";
      }
      if (k === "valor") nf.troco_para = sugerirTrocoPara(v);
      if (isColeta(nf.tipo_id)) { nf.valor = 0; nf.valor_recebido = 0; nf.troco = 0; nf.troco_para = 0; }
      else if (isSemTroco(nf.forma_pgto_id)) { nf.troco = 0; nf.troco_para = 0; nf.valor_recebido = 0; }
      else { nf.troco = calcTroco(nf.valor, nf.valor_recebido, nf.troco_para); }
      return nf;
    });
  };

  // ---- CRUD ----
  const submit = async (clone = false) => {
    // Validação de campos obrigatórios (fev/2026) — regra do usuário:
    // bairro/tipo/pedido/cliente/tel/forma sempre; forma opcional pra Coleta/Transferência.
    const missing = getMissingRequiredFields(form, tipos);
    if (missing.size > 0) {
      return toast.error(buildErrorMessage(missing, form));
    }
    if (formDup?.blocking) {
      return toast.error("Nº de pedido já em uso por entrega ativa — corrija antes de salvar");
    }
    const payload = {
      ...form,
      valor: Number(form.valor) || 0,
      valor_recebido: Number(form.valor_recebido) || 0,
      troco_para: Number(form.troco_para) || 0,
      troco: Number(form.troco) || 0,
    };
    if (!payload.entregador_id) payload.entregador_id = null;
    try {
      await api.post("/entregas", payload);
    } catch (e) {
      // 403 é tratado pelo interceptor global — retorna sem resetar form pra não travar UX.
      const msg = e?.response?.data?.detail;
      if (msg && e?.response?.status !== 403) toast.error(msg);
      return;
    }
    toast.success(clone ? "Entrega salva · janela mantida para adicionar outra" : "Entrega lançada · HR Saída e Data registradas");
    load();
    if (clone) setForm({ ...form, numero_pedido: "", valor: "", valor_recebido: "", troco_para: "", troco: 0, motivo: "", observacao: "" });
    else { setOpen(false); setForm(initial()); }
  };

  const openEdit = (it) => { setEditItem(it); setEditForm({ ...it, valor: it.valor || "", valor_recebido: it.valor_recebido || "", troco_para: it.troco_para || "" }); };
  const doSaveEdit = async (p) => {
    try {
      await api.put(`/entregas/${editItem.id}`, p);
      toast.success("Entrega atualizada"); setEditItem(null); load();
    } catch (e) {
      console.error("Falha ao salvar edit", e);
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };
  const saveEdit = async () => {
    const missing = getMissingRequiredFields(editForm, tipos);
    if (missing.size > 0) {
      return toast.error(buildErrorMessage(missing, editForm));
    }
    if (editDup?.blocking) {
      return toast.error("Nº de pedido já em uso por outra entrega ativa — corrija antes de salvar");
    }
    const p = {
      ...editForm,
      valor: Number(editForm.valor) || 0,
      valor_recebido: Number(editForm.valor_recebido) || 0,
      troco_para: Number(editForm.troco_para) || 0,
      troco: Number(editForm.troco) || 0,
    };
    delete p.id; delete p.created_at; delete p.created_by;
    if (p.entregador_id === "" || p.entregador_id === "__none__") p.entregador_id = null;
    const hrChanged = (p.hr_saida !== editItem.hr_saida) || (p.hr_chegada !== editItem.hr_chegada);
    if (hrChanged) {
      if (!canConfigHorario) { delete p.hr_saida; delete p.hr_chegada; }
      else { setPwdConfirm({ action: () => doSaveEdit(p), message: "Digite sua senha para confirmar a alteração de HR Saída/Chegada:" }); return; }
    } else { delete p.hr_saida; delete p.hr_chegada; }
    doSaveEdit(p);
  };

  const changeStatus = async (it, newStatus) => {
    if (newStatus === "concluida") {
      setStatusPrompt({ entrega: it, newStatus, field: "valor_recebido", value: it.valor || 0, label: "Valor recebido do cliente" });
    } else if (newStatus === "cancelada" || newStatus === "remarcar") {
      setStatusPrompt({ entrega: it, newStatus, field: "motivo", value: "", label: "Motivo * (obrigatório)" });
    } else {
      await api.put(`/entregas/${it.id}`, { status: newStatus });
      toast.success(`Status: ${STATUS[newStatus]}`); load();
    }
  };

  const confirmStatusPrompt = async () => {
    const { entrega, newStatus, field, value } = statusPrompt;
    if ((newStatus === "cancelada" || newStatus === "remarcar") && field === "motivo" && !String(value || "").trim()) {
      return toast.error("Motivo é obrigatório para cancelar ou remarcar");
    }
    const payload = { status: newStatus, [field]: field === "valor_recebido" ? (Number(value) || 0) : value };
    if (field === "valor_recebido" && !isSemTroco(entrega.forma_pgto_id)) {
      payload.troco = calcTroco(entrega.valor, Number(value) || 0, entrega.troco_para);
      payload.valor_recebido = Number(value) || 0;
    }
    await api.put(`/entregas/${entrega.id}`, payload);
    toast.success(`Status alterado para ${STATUS[newStatus]} · HR Chegada registrada`);
    setStatusPrompt(null); load();
  };

  const del = async (id) => {
    try { await api.delete(`/entregas/${id}`); toast.success("Excluída"); load(); }
    catch (e) {
      // 403 já é mostrado pelo interceptor global — não duplica toast genérico.
      if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail || "Erro");
    }
  };
  const askDelete = (id) => setPwdConfirm({ action: () => del(id), message: "Digite sua senha para excluir esta entrega:" });
  const askBulkDelete = () => setPwdConfirm({ action: bulkDeleteSelected, message: `Digite sua senha para excluir ${selected.size} entrega(s) selecionada(s):` });
  const askDeleteAll = () => { setConfirmClear(false); setPwdConfirm({ action: deleteAllVisible, message: `Digite sua senha para excluir ${filtered.length} entrega(s) visível(is):` }); };

  const formCommon = {
    setField, isColeta, isReceitaRevenda, isSemTroco,
    entregadores, bairros, regioes, tipos, formas, rotas, canConfigHorario, todayStr, tomorrowStr,
    user: currentUser, onCatalogAdd,
  };

  const filtersProps = {
    filters, setFilters, entregadores, bairros, rotas, tipos, formas, regioes, programadasCount, setQuickFilter,
  };

  const tableProps = {
    filtered, filters, selected, entregadores, bairros, regioes, tipos, formas, rotas, empresa,
    total, page, pageSize, setPage, setPageSize, loading, sort, toggleSort,
    unreadMap, bulkEntregador, setBulkEntregador, bulkData, setBulkData, bulkRota, setBulkRota, bulkAssignRota, otimizarSelecionadas, otimBusy, nameOf, isColeta, isSemTroco, isReceitaRevenda,
    toggleSelect, toggleSelectAll, clearSelection,
    bulkAssignEntregador, bulkUpdateData, askBulkDelete, setConfirmClear,
    changeStatus, openEdit, askDelete, markRead, setChatEntrega, setEmpresa,
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3" data-testid="page-entregas">
      <EntregasHeader
        canCreate={canCreate}
        onImport={() => setImportOpen(true)}
        onLote={() => setLoteOpen(true)}
        onNova={() => { setForm(initial()); setOpen(true); }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nova Entrega</DialogTitle></DialogHeader>
          <EntregasForm f={form} target="form" {...formCommon} dup={formDup} />
          {(() => {
            const formMissing = getMissingRequiredFields(form, tipos);
            const canSaveForm = formMissing.size === 0 && !formDup?.blocking;
            const disabledMsg = formDup?.blocking
              ? "Corrija duplicidade"
              : formMissing.size > 0
              ? `Faltam: ${formMissing.size} campo(s) obrigatório(s)`
              : null;
            return (
              <>
                {formMissing.size > 0 && (
                  <div
                    className="mt-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                    data-testid="form-missing-required"
                  >
                    {buildErrorMessage(formMissing, form)}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button
                    data-testid="btn-salvar-entrega"
                    onClick={() => submit(false)}
                    disabled={!canSaveForm}
                    className={canSaveForm ? "flex-1 bg-orange-600 hover:bg-orange-700" : "flex-1 bg-slate-400 hover:bg-slate-400 cursor-not-allowed"}
                  >
                    {disabledMsg || "Salvar e Fechar"}
                  </Button>
                  <Button
                    data-testid="btn-clonar-entrega"
                    onClick={() => submit(true)}
                    disabled={!canSaveForm}
                    variant="outline"
                    className="flex-1 border-orange-600 text-orange-700 hover:bg-orange-50 disabled:opacity-50"
                  >
                    <Copy className="w-4 h-4 mr-2" />Salvar e Clonar
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <LoteDialog open={loteOpen} onOpenChange={setLoteOpen}
        entregadores={entregadores} tipos={tipos} formas={formas} bairros={bairros}
        regioes={regioes} rotas={rotas} user={currentUser} onCatalogAdd={onCatalogAdd} items={items}
        onCreated={load} />

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />

      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Entrega</DialogTitle></DialogHeader>
          {editItem && <EntregasForm f={editForm} target="edit" {...formCommon} dup={editDup} />}
          {editItem && <div className="mt-3"><EvidenceViewer entrega={editItem} /></div>}
          {editItem && (() => {
            const editMissing = getMissingRequiredFields(editForm, tipos);
            const canSaveEdit = editMissing.size === 0 && !editDup?.blocking;
            const disabledMsg = editDup?.blocking
              ? "Corrija duplicidade"
              : editMissing.size > 0
              ? `Faltam: ${editMissing.size} campo(s) obrigatório(s)`
              : null;
            return (
              <>
                {editMissing.size > 0 && (
                  <div
                    className="mt-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                    data-testid="edit-missing-required"
                  >
                    {buildErrorMessage(editMissing, editForm)}
                  </div>
                )}
                <Button
                  data-testid="btn-save-edit-entrega"
                  onClick={saveEdit}
                  disabled={!canSaveEdit}
                  className={canSaveEdit ? "w-full bg-orange-600 hover:bg-orange-700 mt-3" : "w-full bg-slate-400 hover:bg-slate-400 cursor-not-allowed mt-3"}
                >
                  {disabledMsg || "Salvar Alterações"}
                </Button>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!statusPrompt} onOpenChange={(o) => !o && setStatusPrompt(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{statusPrompt?.newStatus === "concluida" ? "Concluir Entrega" : `Marcar como ${STATUS[statusPrompt?.newStatus] || ""}`}</DialogTitle></DialogHeader>
          {statusPrompt && (
            <div className="space-y-3">
              <Label>{statusPrompt.label}</Label>
              {statusPrompt.field === "valor_recebido" ? (
                <Input data-testid="prompt-valor-recebido" type="number" step="0.01" autoFocus
                  value={statusPrompt.value} onChange={(e) => setStatusPrompt({ ...statusPrompt, value: e.target.value })}
                  placeholder="R$ 0,00" />
              ) : (
                <Textarea data-testid="prompt-motivo" autoFocus value={statusPrompt.value}
                  onChange={(e) => setStatusPrompt({ ...statusPrompt, value: e.target.value })}
                  placeholder="Descreva o motivo..." rows={4} />
              )}
              {statusPrompt.field === "valor_recebido" && !isSemTroco(statusPrompt.entrega.forma_pgto_id) && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2 text-sm">
                  Valor: <b className="mono">{fmtBRL(statusPrompt.entrega.valor)}</b> · Troco:
                  <b className="mono text-orange-700 ml-1">{fmtBRL(calcTroco(statusPrompt.entrega.valor, statusPrompt.value, statusPrompt.entrega.troco_para))}</b>
                </div>
              )}
              <Button data-testid="btn-confirm-status" onClick={confirmStatusPrompt} className="w-full bg-orange-600 hover:bg-orange-700">Confirmar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EntregasBody filtersProps={filtersProps} tableProps={tableProps} />

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent>
          <DialogHeader><DialogTitle>Limpar todas as entregas visíveis?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Isso excluirá <b>{filtered.length}</b> entrega(s) visíveis nesta página{total > filtered.length ? ` (de ${total} no filtro atual)` : ""}. Esta ação não pode ser desfeita.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setConfirmClear(false)}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={askDeleteAll} data-testid="btn-confirm-clear-all">Sim, excluir tudo</Button>
          </div>
        </DialogContent>
      </Dialog>

      <PasswordConfirm open={!!pwdConfirm} onClose={() => setPwdConfirm(null)}
        onConfirmed={() => { pwdConfirm?.action?.(); }} message={pwdConfirm?.message} />

      <ChatDialog open={!!chatEntrega} onOpenChange={(o) => !o && setChatEntrega(null)}
        entrega={chatEntrega} currentUserId={currentUser?.id}
        title={`Chat · Pedido ${chatEntrega ? buildPedidoLabel(chatEntrega, tipos.find((t) => t.id === chatEntrega.tipo_id)?.nome, "—") : "—"} · ${chatEntrega?.cliente_nome || ""}`} />

      <OtimizarDialog result={otimResult} onClose={() => setOtimResult(null)} />
    </div>
  );
}
